import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../../components/Sidebar";
import { useTranslation } from "react-i18next";
import { createNewTradingAccount, invalidateTradingAccountsCache } from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import { getPermissionControllers, hasCustomerPermission, isAccountApprovalLocked } from "../../../utils/permissionHelper";
import PermissionCustomerModelController from "../../../controller/model/PermissionCustomerModelController";
import "../../css/Add.css";
import createAccountPopopImage from "../../../assets/images/createAccountPopop.png";

const AddAccount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("userData");
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } catch (err) {
      // Error loading user data
    }
  }, []);

  const permissions = useMemo(() => getPermissionControllers(user), [user]);
  const { customerPerm, customerDevicePerm } = permissions;
  const approvalLocked = useMemo(() => isAccountApprovalLocked(user), [user]);
  const canAddRealAccount =
    !approvalLocked &&
    hasCustomerPermission(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__ADD_REAL_ACCOUNT
    );

  const handleCancel = () => {
    navigate("/pages/accounts/list");
  };

  const handleConfirm = async () => {
    if (!canAddRealAccount) return;
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      // ACCOUNT_TYPE: 0 -> Real account
      const response = await createNewTradingAccount(0);
      const data = response?.decryptedData || response?.data || {};
      const code = Number(
        data?.CODE ??
        data?.code ??
        data?.BODY?.CODE ??
        data?.body?.CODE ??
        data?.body?.code
      );

      if (code !== AppConstants.responseOk) {
        const message =
          data?.MESSAGE ||
          data?.message ||
          data?.BODY?.MESSAGE ||
          data?.body?.MESSAGE ||
          data?.body?.message ||
          "Failed to create new Real Account. Please try again.";
        setError(message);
        return;
      }

      invalidateTradingAccountsCache();
      navigate("/pages/accounts/list");
    } catch (err) {
      setError(err?.message || "Failed to create new Real Account. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="accounts-list-page">
      <Sidebar user={user} />
      <div className="accounts-list-content">
        {!canAddRealAccount ? (
          <div className="cash-in-container">
            <div className="cash-in-tab-content">
              <div className="cash-in-locked-message">
                <i className="fa-solid fa-lock"></i>
                <p>{t("no_permission_page", { ns: "mobile" })}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="account-add-page">
            <div className="account-add-card">
              <div className="account-add-icon">
                <img
                  src={createAccountPopopImage}
                  alt={t("add_real_account", { ns: "mobile" })}
                  className="account-add-icon-image"
                />
              </div>
              <h1 className="account-add-title">{t("add_real_account", { ns: "mobile" })}</h1>
              <p className="account-add-message">
                {t("confirm_add_real_account", { ns: "mobile" })}
              </p>
              <div className="account-add-buttons">
                <button
                  type="button"
                  className="account-add-btn account-add-btn-cancel"
                  onClick={handleCancel}
                  disabled={creating}
                >
                  {t("cancel", { ns: "mobile" })}
                </button>
                <button
                  type="button"
                  className="account-add-btn account-add-btn-confirm"
                  onClick={handleConfirm}
                  disabled={creating}
                >
                  {creating ? t("creating", { ns: "mobile" }) : t("yes", { ns: "mobile" })}
                </button>
              </div>
              {error && <div className="account-add-error">{error}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddAccount;

